import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  AMBULANCE_FACILITIES,
  AMBULANCE_TYPES,
  getAmbulancePlaceDetails,
  searchAmbulancePlaces,
} from '../../services/MasterAdmin/ambulanceServiceAreaCompareService';
import { fetchVendors } from '../../services/MasterAdmin/vendorService';
import {
  createMasterAdminAmbulanceBooking,
  updateMasterAdminAmbulanceBooking,
  type CreateMasterAdminAmbulanceBookingPayload,
  type MasterAdminAmbulanceBookingRow,
} from '../../services/MasterAdmin/masterAdminAmbulanceBookingService';

type AgencyOption = { id: string; name: string };
type PlaceSuggestion = { placeId: string; description: string };

type RouteStopDraft = {
  id: string;
  label: string;
  address: string;
  lat?: number;
  lng?: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editBooking?: MasterAdminAmbulanceBookingRow | null;
};

const PRIMARY_FACILITY_VALUES = new Set([
  'WITH_OXYGEN',
  'WITHOUT_OXYGEN',
  'AC_WITH_OXYGEN',
  'AC_WITHOUT_OXYGEN',
]);

const EXTRA_FACILITY_OPTIONS = [
  { value: 'ATTENDANT', label: 'Attendant' },
  { value: 'RETURN_JOURNEY', label: 'Return journey' },
];

function newStopId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function defaultStops(): RouteStopDraft[] {
  return [
    { id: newStopId(), label: 'A', address: '' },
    { id: newStopId(), label: 'B', address: '' },
  ];
}

function chipClass(active: boolean) {
  return `rounded-md border px-2.5 py-1.5 text-xs sm:text-sm transition-colors ${
    active
      ? 'border-blue-600 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-200'
      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200'
  }`;
}

const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white';

function preventNumberScroll(e: React.WheelEvent<HTMLInputElement>) {
  // Prevent mouse-wheel from accidentally changing focused number inputs.
  e.currentTarget.blur();
}

const MasterAdminAmbulanceBookingModal: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  editBooking,
}) => {
  const isEdit = Boolean(editBooking);
  const [agencies, setAgencies] = useState<AgencyOption[]>([]);
  const [agencySearch, setAgencySearch] = useState('');
  const [useCustomAgency, setUseCustomAgency] = useState(false);
  const [vendorId, setVendorId] = useState('');
  const [customAgencyName, setCustomAgencyName] = useState('');

  const [patientName, setPatientName] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [patientGender, setPatientGender] = useState<'MALE' | 'FEMALE' | 'CHILD' | ''>('');

  const [stops, setStops] = useState<RouteStopDraft[]>(defaultStops);
  const [suggestionsByStop, setSuggestionsByStop] = useState<Record<string, PlaceSuggestion[]>>({});
  const [showSuggestionsFor, setShowSuggestionsFor] = useState<string | null>(null);
  const [loadingStopId, setLoadingStopId] = useState<string | null>(null);

  const [ambulanceType, setAmbulanceType] = useState<string>(AMBULANCE_TYPES[0]?.value || '');
  const [primaryFacility, setPrimaryFacility] = useState<string>('WITH_OXYGEN');
  const [extraFacilities, setExtraFacilities] = useState<string[]>([]);
  const [isDeadBody, setIsDeadBody] = useState(false);
  const [pickupTimeType, setPickupTimeType] = useState<'IMMEDIATE' | 'SCHEDULED'>('IMMEDIATE');
  const [scheduledPickupAt, setScheduledPickupAt] = useState('');

  const [baseFare, setBaseFare] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [paymentCollectionType, setPaymentCollectionType] = useState<'FULL' | 'ADVANCE'>('FULL');
  const [advancePaidAmount, setAdvancePaidAmount] = useState('');

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchVendors();
        const list = (response.data?.ambulances || [])
          .map((v) => ({
            id: String(v.vendorId || ''),
            name: String(v.agencyName || v.Vendor?.email || v.vendorId || 'Unnamed agency'),
          }))
          .filter((v) => v.id)
          .sort((a, b) => a.name.localeCompare(b.name));
        if (!cancelled) setAgencies(list);
      } catch (e) {
        console.error(e);
        if (!cancelled) toast.error('Failed to load ambulance agencies');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!editBooking) return;
    setPatientName(editBooking.patientName || '');
    setPatientPhone(editBooking.patientPhone || '');
    setPatientGender((editBooking.patientGender as 'MALE' | 'FEMALE' | 'CHILD' | null) || '');
    setUseCustomAgency(!editBooking.vendorId);
    setVendorId(editBooking.vendorId || '');
    setCustomAgencyName(editBooking.customAgencyName || '');
    setAmbulanceType(editBooking.ambulanceType || AMBULANCE_TYPES[0]?.value || '');
    const facilities = Array.isArray(editBooking.facilities) ? editBooking.facilities : [];
    const primary = facilities.find((f) => PRIMARY_FACILITY_VALUES.has(f));
    setPrimaryFacility(primary || 'WITH_OXYGEN');
    setExtraFacilities([
      ...facilities.filter((f) => EXTRA_FACILITY_OPTIONS.some((x) => x.value === f)),
      ...(editBooking.isReturnJourney ? ['RETURN_JOURNEY'] : []),
    ]);
    setIsDeadBody(Boolean(editBooking.isDeadBody));
    setPickupTimeType(editBooking.pickupTimeType === 'SCHEDULED' ? 'SCHEDULED' : 'IMMEDIATE');
    setScheduledPickupAt(
      editBooking.scheduledPickupAt
        ? new Date(editBooking.scheduledPickupAt).toISOString().slice(0, 16)
        : ''
    );
    setBaseFare(String(editBooking.baseFare || ''));
    setDiscountAmount(String(editBooking.discountAmount || ''));
    setPaymentCollectionType(
      editBooking.paymentCollectionType === 'ADVANCE' ? 'ADVANCE' : 'FULL'
    );
    setAdvancePaidAmount(String(editBooking.advancePaidAmount || ''));

    const existingStops = Array.isArray(editBooking.routeStops)
      ? editBooking.routeStops.filter((s) => s?.address)
      : [];
    if (existingStops.length >= 2) {
      setStops(
        existingStops.map((s, i) => ({
          id: newStopId(),
          label: s.label || String.fromCharCode(65 + i),
          address: s.address,
          lat: s.lat,
          lng: s.lng,
        }))
      );
    } else {
      setStops([
        { id: newStopId(), label: 'A', address: editBooking.pickupAddress || '' },
        { id: newStopId(), label: 'B', address: editBooking.dropAddress || '' },
      ]);
    }
  }, [open, editBooking]);

  const filteredAgencies = useMemo(() => {
    const q = agencySearch.trim().toLowerCase();
    if (!q) return agencies;
    return agencies.filter((a) => a.name.toLowerCase().includes(q));
  }, [agencies, agencySearch]);

  const base = Math.max(0, Number(baseFare) || 0);
  const discount = Math.max(0, Math.min(base, Number(discountAmount) || 0));
  const total = Math.max(0, base - discount);
  const advance =
    paymentCollectionType === 'FULL'
      ? total
      : Math.max(0, Math.min(total, Number(advancePaidAmount) || 0));
  const remaining = Math.max(0, total - advance);

  const resetForm = () => {
    setAgencySearch('');
    setUseCustomAgency(false);
    setVendorId('');
    setCustomAgencyName('');
    setPatientName('');
    setPatientPhone('');
    setPatientGender('');
    setStops(defaultStops());
    setSuggestionsByStop({});
    setShowSuggestionsFor(null);
    setAmbulanceType(AMBULANCE_TYPES[0]?.value || '');
    setPrimaryFacility('WITH_OXYGEN');
    setExtraFacilities([]);
    setIsDeadBody(false);
    setPickupTimeType('IMMEDIATE');
    setScheduledPickupAt('');
    setBaseFare('');
    setDiscountAmount('');
    setPaymentCollectionType('FULL');
    setAdvancePaidAmount('');
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose();
  };

  const updateStop = (id: string, patch: Partial<RouteStopDraft>) => {
    setStops((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const searchStopSuggestions = async (stopId: string, value: string) => {
    const input = value.trim();
    if (input.length < 3) {
      setSuggestionsByStop((prev) => ({ ...prev, [stopId]: [] }));
      return;
    }
    try {
      const suggestions = await searchAmbulancePlaces(input);
      setSuggestionsByStop((prev) => ({ ...prev, [stopId]: suggestions.slice(0, 6) }));
      setShowSuggestionsFor(stopId);
    } catch (e) {
      console.error(e);
    }
  };

  const handleStopAddressChange = (id: string, value: string) => {
    updateStop(id, { address: value, lat: undefined, lng: undefined });
    void searchStopSuggestions(id, value);
  };

  const selectSuggestion = async (stopId: string, placeId: string, fallback: string) => {
    try {
      setLoadingStopId(stopId);
      const details = await getAmbulancePlaceDetails(placeId);
      if (details) {
        updateStop(stopId, {
          address: details.formattedAddress,
          lat: details.latitude,
          lng: details.longitude,
        });
      } else {
        updateStop(stopId, { address: fallback });
      }
    } catch (e) {
      console.error(e);
      updateStop(stopId, { address: fallback });
    } finally {
      setLoadingStopId(null);
      setShowSuggestionsFor(null);
      setSuggestionsByStop((prev) => ({ ...prev, [stopId]: [] }));
    }
  };

  const addStop = () => {
    setStops((prev) => {
      const label = String.fromCharCode(65 + prev.length);
      return [...prev, { id: newStopId(), label, address: '' }];
    });
  };

  const removeStop = (id: string) => {
    setStops((prev) => {
      if (prev.length <= 2) return prev;
      return prev
        .filter((s) => s.id !== id)
        .map((s, i) => ({ ...s, label: String.fromCharCode(65 + i) }));
    });
  };

  const toggleExtra = (value: string) => {
    setExtraFacilities((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleSubmit = async () => {
    if (!patientName.trim()) {
      toast.error('Patient name is required');
      return;
    }
    if (!patientPhone.trim() || patientPhone.trim().length < 8) {
      toast.error('Valid patient phone is required');
      return;
    }
    if (!useCustomAgency && !vendorId) {
      toast.error('Select an agency or switch to custom agency');
      return;
    }
    if (useCustomAgency && !customAgencyName.trim()) {
      toast.error('Enter custom agency name');
      return;
    }
    const filledStops = stops.filter((s) => s.address.trim());
    if (filledStops.length < 2) {
      toast.error('Add at least pickup and drop addresses');
      return;
    }
    if (!ambulanceType) {
      toast.error('Select ambulance type');
      return;
    }
    if (pickupTimeType === 'SCHEDULED' && !scheduledPickupAt) {
      toast.error('Select scheduled date and time');
      return;
    }
    if (base <= 0) {
      toast.error('Enter base fare amount');
      return;
    }
    if (paymentCollectionType === 'ADVANCE' && advance <= 0) {
      toast.error('Enter advance amount');
      return;
    }

    const facilities = [primaryFacility, ...extraFacilities].filter(Boolean);

    const payload: CreateMasterAdminAmbulanceBookingPayload = {
      patientName: patientName.trim(),
      patientPhone: patientPhone.trim(),
      patientGender: patientGender || null,
      vendorId: useCustomAgency ? null : vendorId,
      customAgencyName: useCustomAgency ? customAgencyName.trim() : null,
      routeStops: filledStops.map((s) => ({
        label: s.label,
        address: s.address.trim(),
        lat: s.lat,
        lng: s.lng,
      })),
      ambulanceType,
      facilities,
      isReturnJourney: extraFacilities.includes('RETURN_JOURNEY'),
      isDeadBody,
      pickupTimeType,
      scheduledPickupAt: pickupTimeType === 'SCHEDULED' ? scheduledPickupAt : null,
      baseFare: base,
      discountAmount: discount,
      paymentCollectionType,
      advancePaidAmount: advance,
      paymentMethod: 'CASH',
    };

    try {
      setSaving(true);
      const res = isEdit && editBooking
        ? await updateMasterAdminAmbulanceBooking(editBooking.bookingId, payload)
        : await createMasterAdminAmbulanceBooking(payload);
      if (!res.success) {
        toast.error(res.message || `Failed to ${isEdit ? 'update' : 'create'} booking`);
        return;
      }
      toast.success(`Ambulance booking ${isEdit ? 'updated' : 'created'}`);
      resetForm();
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'create'} booking`);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const primaryFacilities = AMBULANCE_FACILITIES.filter((f) =>
    PRIMARY_FACILITY_VALUES.has(f.value)
  );

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center overflow-y-auto bg-black/50 p-2 sm:items-center sm:p-4">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b px-5 py-4 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isEdit ? 'Edit ambulance booking' : 'Add ambulance booking'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Outside-app booking (call / walk-in). No app user required.
            </p>
          </div>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            onClick={handleClose}
            disabled={saving}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 overflow-y-auto px-5 py-4">
          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Patient</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm text-gray-600 dark:text-gray-300">Name</label>
                <input className={inputClass} value={patientName} onChange={(e) => setPatientName(e.target.value)} placeholder="Patient full name" />
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-600 dark:text-gray-300">Phone</label>
                <input className={inputClass} value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)} placeholder="10-digit mobile" maxLength={15} />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-600 dark:text-gray-300">Gender</label>
              <div className="flex flex-wrap gap-2">
                {([{ value: 'MALE', label: 'Male' }, { value: 'FEMALE', label: 'Female' }, { value: 'CHILD', label: 'Child' }] as const).map((g) => (
                  <button key={g.value} type="button" className={chipClass(patientGender === g.value)} onClick={() => setPatientGender(g.value)}>
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Agency</h4>
              <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <input type="checkbox" checked={useCustomAgency} onChange={(e) => { setUseCustomAgency(e.target.checked); if (e.target.checked) setVendorId(''); }} />
                Custom agency
              </label>
            </div>
            {useCustomAgency ? (
              <input className={inputClass} value={customAgencyName} onChange={(e) => setCustomAgencyName(e.target.value)} placeholder="Type agency name" />
            ) : (
              <div className="space-y-2">
                <input className={inputClass} value={agencySearch} onChange={(e) => setAgencySearch(e.target.value)} placeholder="Search agency…" />
                <select className={inputClass} value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                  <option value="">Select ambulance agency</option>
                  {filteredAgencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Route (pickup → drop)</h4>
              <button type="button" onClick={addStop} className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200">
                <Plus className="h-3.5 w-3.5" />
                Add stop
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Start typing to get Google address suggestions.
            </p>
            <div className="space-y-2">
              {stops.map((stop, index) => (
                <div key={stop.id} className="flex items-start gap-2">
                  <span className="mt-2 w-6 shrink-0 text-center text-sm font-semibold text-gray-500">
                    {stop.label || String.fromCharCode(65 + index)}
                  </span>
                  <div className="relative flex-1">
                    <input
                      className={inputClass}
                      value={stop.address}
                      onChange={(e) => handleStopAddressChange(stop.id, e.target.value)}
                      onFocus={() => setShowSuggestionsFor(stop.id)}
                      placeholder={index === 0 ? 'Pickup address' : index === stops.length - 1 ? 'Drop address' : 'Stop address'}
                    />
                    {loadingStopId === stop.id ? (
                      <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-gray-400" />
                    ) : null}
                    {showSuggestionsFor === stop.id && (suggestionsByStop[stop.id] || []).length > 0 ? (
                      <div className="absolute z-20 mt-1 max-h-44 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow dark:border-gray-600 dark:bg-gray-800">
                        {(suggestionsByStop[stop.id] || []).map((s) => (
                          <button
                            key={s.placeId}
                            type="button"
                            onClick={() => void selectSuggestion(stop.id, s.placeId, s.description)}
                            className="block w-full border-b border-gray-100 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                          >
                            {s.description}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <button type="button" disabled={stops.length <= 2} onClick={() => removeStop(stop.id)} className="mt-1 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-30 dark:hover:bg-gray-700" aria-label="Remove stop">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Trip</h4>
            <select className={inputClass} value={ambulanceType} onChange={(e) => setAmbulanceType(e.target.value)}>
              {AMBULANCE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <div className="flex flex-wrap gap-2">
              {primaryFacilities.map((f) => (
                <button key={f.value} type="button" className={chipClass(primaryFacility === f.value)} onClick={() => setPrimaryFacility(f.value)}>
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {EXTRA_FACILITY_OPTIONS.map((f) => (
                <button key={f.value} type="button" className={chipClass(extraFacilities.includes(f.value))} onClick={() => toggleExtra(f.value)}>
                  {f.label}
                </button>
              ))}
              <button type="button" className={chipClass(isDeadBody)} onClick={() => setIsDeadBody((v) => !v)}>
                Mortuary / dead body
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={chipClass(pickupTimeType === 'IMMEDIATE')} onClick={() => setPickupTimeType('IMMEDIATE')}>Immediate</button>
              <button type="button" className={chipClass(pickupTimeType === 'SCHEDULED')} onClick={() => setPickupTimeType('SCHEDULED')}>Scheduled</button>
            </div>
            {pickupTimeType === 'SCHEDULED' ? (
              <input type="datetime-local" className={inputClass} value={scheduledPickupAt} onChange={(e) => setScheduledPickupAt(e.target.value)} />
            ) : null}
          </section>

          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Payment</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <input type="number" min={0} className={inputClass} value={baseFare} onChange={(e) => setBaseFare(e.target.value)} onWheel={preventNumberScroll} placeholder="Base fare (₹)" />
              <input type="number" min={0} className={inputClass} value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} onWheel={preventNumberScroll} placeholder="Discount (₹)" />
            </div>
            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm dark:bg-gray-700/50">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Total</span>
                <span className="font-semibold text-gray-900 dark:text-white">₹{total.toLocaleString('en-IN')}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={chipClass(paymentCollectionType === 'FULL')} onClick={() => setPaymentCollectionType('FULL')}>Full payment</button>
              <button type="button" className={chipClass(paymentCollectionType === 'ADVANCE')} onClick={() => setPaymentCollectionType('ADVANCE')}>Advance</button>
            </div>
            {paymentCollectionType === 'ADVANCE' ? (
              <div>
                <input type="number" min={0} className={inputClass} value={advancePaidAmount} onChange={(e) => setAdvancePaidAmount(e.target.value)} onWheel={preventNumberScroll} placeholder="Advance paid (₹)" />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Remaining later: ₹{remaining.toLocaleString('en-IN')}
                </p>
              </div>
            ) : null}
          </section>
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-4 dark:border-gray-700">
          <button type="button" disabled={saving} onClick={handleClose} className="rounded-md bg-gray-100 px-4 py-2 text-sm dark:bg-gray-700 dark:text-gray-200">
            Cancel
          </button>
          <button type="button" disabled={saving} onClick={() => void handleSubmit()} className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50">
            {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create booking')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MasterAdminAmbulanceBookingModal;
